// eslint-disable-next-line import/no-cycle
import bffApi, {BffResponse} from '@client/bffApi';
import {
  ClientService,
  GoalResponse,
  GetAttachAccountToGoalOptions,
  GetAttachAccountToGoalResponse,
  GetGoalListOptions,
  GetAllAccountsForGoalIdOptions,
  AddClientGoalsOptions,
  ClientGoalResponse,
  GoalRequestOptions,
  AttachGoalToAccountRequestIdResponse,
  GetInvestmentModelOptions,
  GetInvestmentModelResponse,
} from '@simpleinvest/ms-client-sdk';
// eslint-disable-next-line import/no-cycle
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {GetTargetPortfolioAtGoalLevelOptions, InvestmentService, Portfolio} from '@simpleinvest/ms-investment-sdk';
import {AxiosResponse} from 'axios';
import {SourcePlatform} from '@simpleinvest/ms-core-sdk';
import {SelectedAccountAssetAllocationResponse} from './response/ServiceResponse';

export default {
  getClientServiceInstance(): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async fetchAccounts(options: GetAllAccountsForGoalIdOptions): Promise<any> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.getAllAccountsForGoalId(options);
  },
  updateAssetAllocation(accountIds: string[]): Promise<AxiosResponse<BffResponse<SelectedAccountAssetAllocationResponse>>> {
    return bffApi
      .getAxiosInstance()
      .post<BffResponse<SelectedAccountAssetAllocationResponse>>(
        `${bffApi.getBaseUrl(true)}/asset-allocation/accounts`,
        accountIds
      );
  },
  /**
   * This API is used for associating/ creating goal for given client.
   *
   * @async
   * @param options - A {@link AddClientGoalsOptions} object.
   * @returns A promise of a {@link ClientGoalResponse} object.
   */
  async associateGoalWithClient(options: AddClientGoalsOptions): Promise<ClientGoalResponse> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.addClientGoals(options);
  },

  /**
   * This API is used for disassociating/ removing goal for given client.
   * @param {goalId} payload
   */
  async disassociateGoalWithClient(payload: GoalRequestOptions): Promise<GoalResponse> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.updateGoal(payload);

    return response;
  },
  /**
   * This API is used for updating existing goal for given client.
   *
   * @async
   * @param options - A {@link GoalRequestOptions} object.
   * @returns A promise of a {@link GoalResponse} object.
   */
  async updateClientGoal(options: GoalRequestOptions): Promise<GoalResponse> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.updateGoal(options);
  },

  async saveChosenGoal(options: {
    segmentGoalId: string;
    goalId: string;
    targetDate: string;
    retirementAge: string;
    displayType: string;
    portfolioType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any> {
    const {segmentGoalId, goalId, targetDate, retirementAge, displayType, portfolioType} = options;

    const service: ClientService = await this.getClientServiceInstance();
    let response;

    try {
      response = await service.getChosenClientGoal({
        segmentGoalId,
        goalId,
        targetDate,
        retirementAge,
        displayType,
        portfolioType,
      });
    } catch (error) {
      if (error.microserviceStatusCode === 409) {
        // goal was already associated with the segment ID
        response = error;
      } else if (error.microserviceStatusCode === 500) {
        throw new Error(error.microserviceStatusCode);
      } else {
        throw new Error(error.microserviceResponseMessage);
      }
    }

    return response;
  },

  async attachGoalToAccountRequestId(options: {
    goalId: number;
    accountRequestId: number;
    clUserId: number;
  }): Promise<AttachGoalToAccountRequestIdResponse> {
    const {goalId, accountRequestId, clUserId} = options;

    try {
      const service: ClientService = await this.getClientServiceInstance();

      const response = await service.attachGoalToAccountRequestId({
        goalId,
        accountRequestId,
        clUserId,
      });

      return response;
    } catch (error) {
      throw new Error(error.microserviceResponseMessage);
    }
  },

  async updateGoal(options: GoalRequestOptions): Promise<GoalResponse> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.updateGoal(options);

    return response;
  },

  async attachAccountToGoal(payload: GetAttachAccountToGoalOptions): Promise<GetAttachAccountToGoalResponse> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.getAttachAccountToGoal(payload);

    return response;
  },

  async getClientGoal(clUserId: string): Promise<unknown> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.getGoalListForClientId({clUserId});

    return response;
  },

  async getAccountsUnAttachedwithGoal(clUserId: number, goalId: number): Promise<unknown> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.getAccountsUnAttachedwithGoal({clUserId, goalId});

    return response;
  },

  async getGoalDetails(goalId: string): Promise<unknown> {
    const service: ClientService = await this.getClientServiceInstance();
    const response = await service.getGoalSummary({goalId});

    return response;
  },

  getInvestmentServiceInstance({logger = getLogger()} = {}): InvestmentService {
    return new InvestmentService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger,
    });
  },
  async getTargetPortfolio(options: GetTargetPortfolioAtGoalLevelOptions): Promise<Portfolio[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getTargetPortfolioAtGoalLevel(options);
  },
  async getGoalSelection(options: GetGoalListOptions): Promise<unknown> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.getGoalList(options);
  },
  async getCurrentPortfolio(goalId: string): Promise<Portfolio[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getCurrentPortfolioAtGoalLevel({goalId});
  },
  async getInvestmentModel(options: GetInvestmentModelOptions): Promise<GetInvestmentModelResponse> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.getInvestmentModel(options);
  },
};
